import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  TextInput,
  Group,
  Table,
  Text,
  Badge,
  Stack,
  Button,
  Modal,
  Chip,
  Card,
  Divider,
  ScrollArea,
  Loader,
  Pagination,
  Grid,
  Flex,
  Title,
  Box,
} from "@mantine/core";
import { IconSearch, IconEye, IconCalendar } from "@tabler/icons-react";
import { AppDispatch, RootState } from "../store";
import { fetchOrders } from "../store/slices/ordersSlice";
import dayjs from "dayjs";
import { OrderResponse } from "types/order.types";
import { OrderDetails } from "components/OrderDetails";
import DatePickerWithShift from "components/DatePickerWithShift";
import { getOrderIdLink } from "utils/order.utils";

const Orders = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: orders, loading } = useSelector(
    (state: RootState) => state.orders,
  );
  const [search, setSearch] = useState("");
  const [date, setDateRange] = useState<Date | null>(new Date());
  const [selectedOrder, setSelectedOrder] = useState<OrderResponse | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;

  // Only fetch orders on mount or date change
  useEffect(() => {
    const params = {
      search: "", // Don't send search param
      startDate: date ? dayjs(date).format("YYYY-MM-DD") : undefined,
    };
    dispatch(fetchOrders(params));
  }, [date, dispatch]);

  // Filter orders in memory based on search
  const filteredOrders = useMemo(() => {
    if (!search) return orders;
    const lower = search.toLowerCase();
    // TODO: complete search logic
    return orders.filter((order) =>
      (
        order.additionalData?.label +
        " " +
        order.customer?.displayName +
        " " +
        ""
      )
        .toLowerCase()
        .includes(lower),
    );
  }, [orders, search]);

  // Slice filtered orders for pagination
  const paginatedOrders = filteredOrders.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage,
  );

  // Reset to first page on search/date change
  useEffect(() => {
    setActivePage(1);
  }, [search, date]);

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: "yellow",
      in_progress: "blue",
      delivered: "green",
      cancelled: "red",
    };
    return colors[status] || "gray";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handleViewOrder = (order: OrderResponse) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setIsModalOpen(false);
  };

  return (
    <Container size="xl" py="xl">
      <Stack>
        <Group justify="space-between" align="center">
          <Title order={2}>Orders</Title>
        </Group>
        <Group justify="space-between">
          <Grid justify="end" align="center" w={"100%"}>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <TextInput
                placeholder="Search orders..."
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                w={"100%"}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <Box style={{ flexGrow: 1 }}>
                <DatePickerWithShift
                  placeholder="Pick the date"
                  value={date}
                  onChange={setDateRange}
                  clearable
                  disabled={loading}
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Flex w={"100%"} justify={"end"}>
                <Pagination
                  total={Math.ceil(filteredOrders.length / itemsPerPage)}
                  value={activePage}
                  onChange={setActivePage}
                />
              </Flex>
            </Grid.Col>
          </Grid>
        </Group>

        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Order Date</Table.Th>
                {/* <Table.Th>Total Amount</Table.Th> */}
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr bg={"transparent"}>
                  <Table.Td colSpan={6} align="center">
                    <Stack
                      align="center"
                      justify="center"
                      style={{ height: "50vh" }}
                    >
                      <Loader />
                      <Text ta="center" c="dimmed">
                        Fetching orders
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginatedOrders.map((order) => (
                  <Table.Tr key={order.orderId}>
                    <Table.Td>{getOrderIdLink(order)}</Table.Td>
                    <Table.Td>{order.customer?.displayName}</Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(order.status)}>
                        {order.status.replace("_", " ").toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {dayjs(order.orderedAt).format("MMM D, YYYY")}
                    </Table.Td>
                    {/* <Table.Td>
                      {formatCurrency(
                        order.orderItems.reduce(
                          (total, item) => total + (item.rate ?? 0),
                          0
                        )
                      )}
                    </Table.Td> */}
                    <Table.Td>
                      <Button
                        variant="subtle"
                        size="sm"
                        leftSection={<IconEye size={16} />}
                        onClick={() => handleViewOrder(order)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>

      {selectedOrder && (
        <Modal
          opened={isModalOpen}
          onClose={closeModal}
          title={`Order Details`}
          size="lg"
        >
          <OrderDetails
            order={selectedOrder}
            formatCurrency={formatCurrency}
            getStatusColor={getStatusColor}
          />
        </Modal>
      )}
    </Container>
  );
};

export default Orders;
